// Grid utilities for Build Pro

export const gridToArray = (gridObj) => {
  return Object.entries(gridObj).map(([key, letter]) => {
    const [row, col] = key.split(',').map(Number);
    return { row, col, letter };
  });
};

export const arrayToGrid = (arr) => {
  const grid = {};
  arr.forEach(({ row, col, letter }) => {
    grid[`${row},${col}`] = letter;
  });
  return grid;
};

export const getCellKey = (row, col) => `${row},${col}`;
export const parseKey = (key) => {
  const [row, col] = key.split(',').map(Number);
  return { row, col };
};

export const getGridBounds = (grid) => {
  if (Object.keys(grid).length === 0) {
    return { minRow: 0, maxRow: 0, minCol: 0, maxCol: 0 };
  }

  let minRow = Infinity,
    maxRow = -Infinity,
    minCol = Infinity,
    maxCol = -Infinity;

  Object.keys(grid).forEach((key) => {
    const { row, col } = parseKey(key);
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
  });

  return { minRow, maxRow, minCol, maxCol };
};

export const isAdjacentToGrid = (row, col, grid) => {
  if (Object.keys(grid).length === 0) return true; // First tile can go anywhere

  const adjacentCells = [
    getCellKey(row - 1, col),
    getCellKey(row + 1, col),
    getCellKey(row, col - 1),
    getCellKey(row, col + 1)
  ];

  return adjacentCells.some((key) => grid[key] !== undefined);
};

export const isGridConnected = (grid) => {
  const cells = Object.keys(grid);
  if (cells.length === 0) return true;

  // BFS from first cell
  const visited = new Set();
  const queue = [cells[0]];
  visited.add(cells[0]);

  while (queue.length > 0) {
    const key = queue.shift();
    const { row, col } = parseKey(key);

    const neighbors = [
      getCellKey(row - 1, col),
      getCellKey(row + 1, col),
      getCellKey(row, col - 1),
      getCellKey(row, col + 1)
    ];

    neighbors.forEach((neighborKey) => {
      if (grid[neighborKey] !== undefined && !visited.has(neighborKey)) {
        visited.add(neighborKey);
        queue.push(neighborKey);
      }
    });
  }

  return visited.size === cells.length;
};

export const extractWords = (grid, minLength = 2) => {
  const words = [];
  const processed = new Set();

  if (Object.keys(grid).length === 0) return words;

  // Extract horizontal words
  const bounds = getGridBounds(grid);
  const addWord = (word, cells, direction) => {
    if (word.length < minLength) return;

    const wordKey = cells.map((c) => `${c.row},${c.col}`).join('|');
    if (processed.has(wordKey)) return;

    words.push({
      word,
      cells: cells.map((cell) => ({ ...cell })),
      direction
    });
    processed.add(wordKey);
  };

  for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
    let word = '';
    let startCol = null;
    const cells = [];

    for (let col = bounds.minCol; col <= bounds.maxCol + 1; col++) {
      const key = getCellKey(row, col);
      if (grid[key]) {
        if (startCol === null) startCol = col;
        word += grid[key];
        cells.push({ row, col });
      } else {
        addWord(word, cells, 'horizontal');
        word = '';
        startCol = null;
        cells.length = 0;
      }
    }
  }

  // Extract vertical words
  for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
    let word = '';
    let startRow = null;
    const cells = [];

    for (let row = bounds.minRow; row <= bounds.maxRow + 1; row++) {
      const key = getCellKey(row, col);
      if (grid[key]) {
        if (startRow === null) startRow = row;
        word += grid[key];
        cells.push({ row, col });
      } else {
        addWord(word, cells, 'vertical');
        word = '';
        startRow = null;
        cells.length = 0;
      }
    }
  }

  return words;
};

export const shiftGrid = (grid, direction) => {
  const newGrid = { ...grid };
  const bounds = getGridBounds(grid);

  // Check if shift is valid
  if (direction === 'left' && bounds.minCol <= -1000) return grid;
  if (direction === 'right' && bounds.maxCol >= 1000) return grid;
  if (direction === 'up' && bounds.minRow <= -1000) return grid;
  if (direction === 'down' && bounds.maxRow >= 1000) return grid;

  // Perform shift
  Object.keys(grid).forEach((key) => {
    const { row, col } = parseKey(key);
    let newRow = row,
      newCol = col;

    if (direction === 'left') newCol -= 1;
    else if (direction === 'right') newCol += 1;
    else if (direction === 'up') newRow -= 1;
    else if (direction === 'down') newRow += 1;

    delete newGrid[key];
    newGrid[getCellKey(newRow, newCol)] = grid[key];
  });

  return newGrid;
};
